import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Container from '@material-ui/core/Container';
import Toolbar from '@material-ui/core/Toolbar';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import SearchIcon from '@material-ui/icons/Search';
import NavbarSearch from '../Search/NavbarSearch';
import { getMainPortalUrl as mainPortalUrl, menuFilters } from '../../subdomains/portal';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import LanguageIcon from '@material-ui/icons/Language';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';

import LogoImg from '../Logo/lgfinal.png';
import { primaryNavigation, relatedSites, languages } from './navigation';
import { getStrapiBackend } from '../Contentful_API/strapiBackend';
import { isProcurementItem, useOpenCounts } from './openCounts';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
    /* Utility bar */
    utilityBar: {
        backgroundColor: tokens.blue900,
        color: 'rgba(255,255,255,0.86)',
        fontSize: '0.8125rem',
    },
    utilityInner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 38,
        gap: theme.spacing(2),
    },
    officialNotice: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: '0.75rem',
        letterSpacing: '0.01em',
        [theme.breakpoints.down('xs')]: {
            fontSize: '0.6875rem',
        },
    },
    utilityLinks: {
        display: 'none',
        alignItems: 'center',
        gap: theme.spacing(2.5),
        [theme.breakpoints.up('md')]: {
            display: 'flex',
        },
    },
    utilityLink: {
        color: 'rgba(255,255,255,0.86)',
        textDecoration: 'none',
        fontSize: '0.75rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        '&:hover': { color: '#ffffff', textDecoration: 'underline' },
    },
    langButton: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: '0.75rem',
        padding: '2px 8px',
        minWidth: 0,
    },

    /* Masthead */
    appBar: {
        backgroundColor: tokens.white,
        color: tokens.ink,
        boxShadow: 'none',
        borderBottom: `1px solid ${tokens.border}`,
    },
    masthead: {
        minHeight: 92,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        paddingLeft: 0,
        paddingRight: 0,
        [theme.breakpoints.down('sm')]: {
            minHeight: 72,
        },
    },
    brand: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        textDecoration: 'none',
        color: 'inherit',
    },
    brandLogo: {
        height: 62,
        width: 'auto',
        [theme.breakpoints.down('sm')]: { height: 48 },
    },
    brandText: {
        display: 'none',
        borderLeft: `1px solid ${tokens.border}`,
        paddingLeft: theme.spacing(2),
        [theme.breakpoints.up('md')]: { display: 'block' },
    },
    brandName: {
        fontFamily: tokens.headingFont,
        fontWeight: 700,
        fontSize: '1.0625rem',
        lineHeight: 1.25,
        color: tokens.blue900,
        margin: 0,
    },
    brandCountry: {
        fontSize: '0.75rem',
        color: tokens.muted,
        margin: 0,
        letterSpacing: '0.02em',
    },
    mastheadActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    mobileOnly: {
        [theme.breakpoints.up('md')]: { display: 'none' },
    },

    /* Primary nav */
    navBar: {
        backgroundColor: tokens.blue600,
        display: 'none',
        [theme.breakpoints.up('md')]: { display: 'block' },
    },
    navInner: {
        display: 'flex',
        alignItems: 'stretch',
        minHeight: 50,
    },
    navButton: {
        color: '#ffffff',
        borderRadius: 0,
        padding: theme.spacing(0, 2),
        fontSize: '0.9375rem',
        fontWeight: 600,
        height: 50,
        borderBottom: '3px solid transparent',
        '&:hover': {
            backgroundColor: tokens.blue800,
            borderBottomColor: tokens.gold,
        },
    },
    navButtonActive: {
        backgroundColor: tokens.blue800,
        borderBottomColor: tokens.gold,
    },
    navSpacer: { flexGrow: 1 },
    /* Live counts: open tenders and open public consultations */
    countBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 20,
        height: 20,
        marginLeft: 6,
        padding: '0 6px',
        borderRadius: 10,
        backgroundColor: tokens.gold,
        color: tokens.blue900,
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1,
    },
    consultPill: {
        alignSelf: 'center',
        color: '#ffffff',
        fontSize: '0.8125rem',
        fontWeight: 700,
        height: 34,
        padding: theme.spacing(0, 1.75),
        borderRadius: 17,
        border: `1px solid ${tokens.gold}`,
        backgroundColor: 'rgba(200, 160, 42, 0.16)',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: 'rgba(200, 160, 42, 0.3)' },
    },
    liveDot: {
        position: 'relative',
        width: 8,
        height: 8,
        marginRight: 8,
        borderRadius: '50%',
        backgroundColor: '#4ade80',
        '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: '#4ade80',
            animation: '$livePulse 1.8s ease-out infinite',
        },
        '@media (prefers-reduced-motion: reduce)': {
            '&::after': { animation: 'none' },
        },
    },
    '@keyframes livePulse': {
        '0%': { transform: 'scale(1)', opacity: 0.7 },
        '100%': { transform: 'scale(2.6)', opacity: 0 },
    },
    megaHighlight: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        margin: `0 0 ${theme.spacing(2)}px`,
        padding: theme.spacing(1, 1.5),
        borderRadius: tokens.radius,
        backgroundColor: tokens.blue50,
        border: `1px solid ${tokens.blue100}`,
        color: tokens.blue900,
        fontSize: '0.875rem',
        fontWeight: 600,
        textDecoration: 'none',
        '&:hover': { backgroundColor: tokens.blue100 },
    },
    srOnly: {
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
    },
    navPortalLink: {
        color: '#ffffff',
        fontSize: '0.875rem',
        fontWeight: 600,
        padding: theme.spacing(0, 2),
    },

    /* Mega menu */
    popper: {
        zIndex: theme.zIndex.appBar + 1,
        width: '100%',
        left: '0 !important',
    },
    megaPanel: {
        borderRadius: 0,
        borderTop: `3px solid ${tokens.gold}`,
        boxShadow: tokens.shadowLg,
        padding: theme.spacing(4, 0),
    },
    megaIntro: {
        paddingRight: theme.spacing(4),
        borderRight: `1px solid ${tokens.border}`,
        [theme.breakpoints.down('sm')]: { borderRight: 'none' },
    },
    megaIntroTitle: {
        marginBottom: theme.spacing(1),
        color: tokens.blue900,
    },
    megaIntroText: {
        color: tokens.muted,
        fontSize: '0.875rem',
        lineHeight: 1.65,
        margin: `0 0 ${theme.spacing(2)}px`,
    },
    megaColumnTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.blue600,
        marginBottom: theme.spacing(1.5),
        paddingBottom: theme.spacing(1),
        borderBottom: `1px solid ${tokens.border}`,
    },
    megaList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
    },
    megaItem: {
        marginBottom: theme.spacing(1.25),
    },
    megaLink: {
        color: tokens.inkSoft,
        textDecoration: 'none',
        fontSize: '0.875rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        '&:hover': { color: tokens.blue600, textDecoration: 'underline' },
    },
    externalIcon: {
        fontSize: 13,
        color: tokens.muted,
    },

    /* Mobile drawer */
    drawerPaper: {
        width: 320,
        maxWidth: '86vw',
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        backgroundColor: tokens.blue900,
        color: '#fff',
    },
    drawerTitle: {
        fontFamily: tokens.headingFont,
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
    drawerSectionLabel: {
        padding: theme.spacing(2, 2, 1),
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.muted,
    },
    drawerSubLink: {
        paddingLeft: theme.spacing(4),
    },
}));

/** Renders either a router link or an external anchor, as the data dictates. */
function NavLink({ link, className, onClick, iconClassName }) {
    const { t } = useTranslation();

    if (link.href) {
        return (
            <a
                className={className}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
            >
                {t(link.label)}
                <OpenInNewIcon className={iconClassName} />
            </a>
        );
    }

    return (
        <RouterLink className={className} to={link.to} onClick={onClick}>
            {t(link.label)}
        </RouterLink>
    );
}

const unwrapRelationData = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    if (Array.isArray(value.data)) return value.data;
    if (value.data) return [value.data];
    if (Array.isArray(value.fields)) return value.fields;
    if (value.fields) return [value.fields];
    return [value];
};

const getRelationAttributes = (relation) => {
    if (!relation) return {};
    if (relation.attributes) return relation.attributes;
    if (relation.fields) return relation.fields;
    if (relation.data) return getRelationAttributes(relation.data);
    return relation;
};

const getMenuText = (fields = {}) => {
    return (
        fields.title ||
        fields.name ||
        fields.menuName ||
        fields.subMName ||
        'Untitled menu'
    );
};

const getMenuSlug = (fields = {}) => {
    return fields.slug || fields.slugMName || fields.Slug || fields.name || '';
};

const getMenuLink = (fields = {}) => {
    const value =
        fields.Link ||
        fields.link ||
        fields.subMLink ||
        fields.submlink ||
        fields.href ||
        fields.url ||
        '';

    if (typeof value !== 'string') return '';
    const link = value.trim();
    return link === '#' || link === '#/' ? '' : link;
};

const normalizeSubMenu = (entry) => {
    const fields = entry?.fields || entry || {};
    const slug = getMenuSlug(fields);
    const directLink = getMenuLink(fields);
    const parentRelation =
        fields.main_menu ||
        fields.mainMenu ||
        fields.parent ||
        null;

    const parentAttributes = getRelationAttributes(parentRelation);
    const parentKey =
        parentAttributes.slug ||
        parentAttributes.menuName ||
        parentAttributes.name ||
        parentRelation?.slug ||
        parentRelation?.menuName ||
        parentRelation?.name ||
        fields.main_menu?.slug ||
        fields.main_menu?.fields?.menuName ||
        fields.main_menu?.attributes?.menuName ||
        '';

    const isExternal = /^https?:\/\//i.test(directLink) || /^mailto:/i.test(directLink);

    const destination = directLink || (slug ? `/pagedetails/${slug}` : '');

    return {
        id: entry?.sys?.id || slug || fields.title || 'submenu',
        label: getMenuText(fields),
        slug,
        parentKey,
        type: parentKey,
        ...(isExternal ? { href: directLink } : { to: destination }),
        description: fields.description || fields.content || '',
    };
};

const normalizeMainMenu = (entry) => {
    const fields = entry?.fields || entry || {};
    const slug = getMenuSlug(fields);
    const directLink = getMenuLink(fields);
    const isExternal = /^https?:\/\//i.test(directLink) || /^mailto:/i.test(directLink);

    return {
        id: entry?.sys?.id || slug || fields.title || 'mainmenu',
        label: getMenuText(fields),
        slug,
        ...(isExternal ? { href: directLink } : { to: directLink || `/pagedetails/${slug}` }),
        description: fields.description || '',
    };
};

const chunkArray = (items, size) => {
    if (!items.length || size <= 0) return [items];
    const output = [];
    for (let i = 0; i < items.length; i += size) {
        output.push(items.slice(i, i + size));
    }
    return output;
};

const buildDynamicNavigation = (mainMenus, subMenus) => {
    if (!Array.isArray(mainMenus) || !mainMenus.length) {
        return primaryNavigation;
    }

    const filteredMainMenus = mainMenus.filter((entry) => {
        const fields = entry?.fields || entry || {};
        return fields.Activate !== false && fields.activate !== false;
    });

    const directSubMenus = new Map();
    subMenus.forEach((entry) => {
        const submenu = normalizeSubMenu(entry);
        const parentKey = submenu.parentKey || submenu.type || submenu.slug;
        if (!parentKey) return;
        const current = directSubMenus.get(parentKey) || [];
        current.push(submenu);
        directSubMenus.set(parentKey, current);
    });

    return filteredMainMenus.map((entry) => {
        const mainMenu = normalizeMainMenu(entry);
        const rawRelationItems = unwrapRelationData(
            entry?.fields?.sub_menus ||
            entry?.fields?.subMenus ||
            entry?.sub_menus ||
            entry?.subMenus ||
            []
        );

        const relationItems = rawRelationItems
            .map((item) => (item?.fields ? item : { fields: item?.attributes || item }))
            .filter(Boolean);

        const related = relationItems.length
            ? relationItems.map(normalizeSubMenu).sort((a, b) => (a.label || '').localeCompare(b.label || ''))
            : (directSubMenus.get(mainMenu.slug) || directSubMenus.get(mainMenu.id) || []).sort((a, b) =>
                (a.label || '').localeCompare(b.label || '')
            );

        const columns = related.length
            ? chunkArray(related, 4).map((group, index) => ({
                  title: index === 0 ? mainMenu.label : `${mainMenu.label} ${index + 1}`,
                  links: group.map((link) => ({
                      label: link.label,
                      ...(link.href ? { href: link.href } : { to: link.to }),
                  })),
              }))
            : undefined;

        return {
            id: mainMenu.id,
            label: mainMenu.label,
            ...(mainMenu.href ? { href: mainMenu.href } : { to: mainMenu.to }),
            description: mainMenu.description,
            columns,
        };
    });
};

export default function Navbar({ subdomain }) {
    const classes = useStyles();
    const history = useHistory();
    const { t, i18n } = useTranslation();

    const [dynamicNavigation, setDynamicNavigation] = useState(primaryNavigation);
    // True on a subdomain whose Site has its own menus: their links stay on the subdomain.
    const [menusAreLocal, setMenusAreLocal] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorNode, setAnchorNode] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expandedSection, setExpandedSection] = useState(null);
    const [langAnchor, setLangAnchor] = useState(null);
    const [language, setLanguage] = useState(
        languages.find((item) => item.code === i18n.language) || languages[0]
    );
    const closeTimer = useRef(null);
    const navBarRef = useRef(null);
    const openCounts = useOpenCounts();
    const openTenders = openCounts.tenders || 0;
    const openConsultations = openCounts.consultations || 0;
    const tenderCountLabel = `${openTenders} ${t(openTenders === 1 ? 'open tender' : 'open tenders')}`;
    const consultationCountLabel = `${openConsultations} ${t('open for comment')}`;

    useEffect(() => {
        let isMounted = true;

        // Menus of one site: the subdomain's own (Main Menu linked to its Site) or, with no
        // subdomain, the main portal's (Main Menu with no Site).
        const fetchMenus = async (site) => {
            const apiBase = getStrapiBackend().apiUrl;
            const filters = menuFilters(site);
            const [mainResponse, subResponse] = await Promise.all([
                fetch(`${apiBase}/main-menus?populate[sub_menus][populate]=*&${filters.main}`, { cache: 'no-store' }),
                fetch(`${apiBase}/sub-menus?populate[main_menu][populate]=*&${filters.sub}`, { cache: 'no-store' })
            ]);

            if (!mainResponse.ok && !subResponse.ok) {
                throw new Error('Menu endpoints unavailable');
            }

            const [mainJson, subJson] = await Promise.all([mainResponse.json(), subResponse.json()]);
            return {
                mainMenus: Array.isArray(mainJson?.data) ? mainJson.data : [],
                subMenus: Array.isArray(subJson?.data) ? subJson.data : [],
            };
        };

        const loadMenus = async () => {
            try {
                let local = false;
                let menus = null;
                if (subdomain) {
                    menus = await fetchMenus(subdomain);
                    local = menus.mainMenus.length > 0;
                }
                // A subdomain without its own menus shows the main portal's, linking back to it.
                if (!local) menus = await fetchMenus(null);
                const { mainMenus, subMenus } = menus;

                if (!isMounted) return;
                setMenusAreLocal(local);

                const normalizedMainMenus = mainMenus.map((item) => ({
                    sys: { id: String(item.id) },
                    fields: item.attributes,
                }));

                const normalizedSubMenus = subMenus.map((item) => ({
                    sys: { id: String(item.id) },
                    fields: item.attributes,
                }));

                const currentNavigation = buildDynamicNavigation(normalizedMainMenus, normalizedSubMenus);
                if (Array.isArray(currentNavigation) && currentNavigation.length > 0) {
                    setDynamicNavigation(currentNavigation);
                    return;
                }
            } catch (error) {
                // Fall back to the static navigation when the CMS is unavailable.
            }

            if (isMounted) {
                setDynamicNavigation(primaryNavigation);
            }
        };

        loadMenus();

        return () => {
            isMounted = false;
        };
    }, [subdomain]);

    const getMainPortalUrl = useCallback(
        (path) => mainPortalUrl(subdomain, path),
        [subdomain]
    );
    // Menu links: the subdomain's own menus stay on the subdomain; otherwise go to the main portal.
    const menuLinkUrl = useCallback(
        (path) => (menusAreLocal ? path : mainPortalUrl(subdomain, path)),
        [menusAreLocal, subdomain]
    );

    const openMenu = (item, node) => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        if (!item.columns) {
            setOpenMenuId(null);
            return;
        }
        setAnchorNode(node);
        setOpenMenuId(item.id);
    };

    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpenMenuId(null), 140);
    };

    const cancelClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    };

    const closeMenu = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpenMenuId(null);
    };

    const navigateSearch = (path) => {
        setDrawerOpen(false);
        if (subdomain) {
            window.location.href = getMainPortalUrl(path);
            return;
        }
        history.push(path);
    };
    const activeItem = dynamicNavigation.find((item) => item.id === openMenuId);

    return (
        <div>
            {/* Utility bar: identifies the site as official and links sister agencies */}
            <div className={classes.utilityBar}>
                <Container maxWidth="lg">
                    <div className={classes.utilityInner}>
                        <span className={classes.officialNotice}>
                            <AccountBalanceIcon style={{ fontSize: 15 }} />
                            {t('An official website of the Democratic Republic of Timor-Leste')}
                        </span>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <nav className={classes.utilityLinks} aria-label="Related government sites">
                                {relatedSites.map((site) => (
                                    <a
                                        key={site.label}
                                        className={classes.utilityLink}
                                        href={site.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t(site.label)}
                                        <OpenInNewIcon style={{ fontSize: 12 }} />
                                    </a>
                                ))}
                            </nav>

                            <Button
                                className={classes.langButton}
                                onClick={(e) => setLangAnchor(e.currentTarget)}
                                startIcon={<LanguageIcon style={{ fontSize: 16 }} />}
                                aria-haspopup="true"
                                aria-label={t('Change language, current language {{label}}', { label: t(language.label) })}
                            >
                                {language.short}
                            </Button>
                            <Menu
                                anchorEl={langAnchor}
                                open={Boolean(langAnchor)}
                                onClose={() => setLangAnchor(null)}
                                getContentAnchorEl={null}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                {languages.map((lang) => (
                                    <MenuItem
                                        key={lang.code}
                                        selected={lang.code === language.code}
                                        onClick={() => {
                                            setLanguage(lang);
                                            i18n.changeLanguage(lang.code);
                                            window.localStorage.setItem('mof-language', lang.code);
                                            setLangAnchor(null);
                                        }}
                                    >
                                        {t(lang.label)}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </div>
                    </div>
                </Container>
            </div>

            {/* Masthead: crest, name of the institution, and portal search */}
            <AppBar position="sticky" className={classes.appBar}>
                <Container maxWidth="lg">
                    <Toolbar className={classes.masthead} disableGutters>
                        <a className={classes.brand} href={getMainPortalUrl('/')}>
                            <img src={LogoImg} alt="" className={classes.brandLogo} />
                            <span className={classes.brandText}>
                                <p className={classes.brandName}>Ministry of Finance</p>
                                <p className={classes.brandCountry}>
                                    Democratic Republic of Timor-Leste
                                </p>
                            </span>
                        </a>

                        <div className={classes.mastheadActions}>
                            <NavbarSearch id="portal-search" onNavigate={navigateSearch} />

                            <IconButton
                                className={classes.mobileOnly}
                                aria-label="Search"
                                onClick={() => setDrawerOpen(true)}
                            >
                                <SearchIcon />
                            </IconButton>

                            <IconButton
                                className={classes.mobileOnly}
                                aria-label="Open main menu"
                                aria-expanded={drawerOpen}
                                onClick={() => setDrawerOpen(true)}
                            >
                                <MenuIcon />
                            </IconButton>
                        </div>
                    </Toolbar>
                </Container>

                {/* Primary navigation with hover / focus mega menus */}
                <div className={classes.navBar} ref={navBarRef}>
                    <Container maxWidth="lg">
                        <nav className={classes.navInner} aria-label="Main navigation">
                            {dynamicNavigation.map((item) => (
                                <Button
                                    key={item.id}
                                    className={`${classes.navButton} ${
                                        openMenuId === item.id ? classes.navButtonActive : ''
                                    }`}
                                    href={item.href || menuLinkUrl(item.to)}
                                    aria-haspopup={item.columns ? 'true' : undefined}
                                    aria-expanded={item.columns ? openMenuId === item.id : undefined}
                                    endIcon={
                                        item.columns ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : null
                                    }
                                    onMouseEnter={(e) => openMenu(item, navBarRef.current || e.currentTarget)}
                                    onMouseLeave={scheduleClose}
                                    onFocus={(e) => openMenu(item, navBarRef.current || e.currentTarget)}
                                >
                                    {t(item.label)}
                                    {isProcurementItem(item) && openTenders > 0 && (
                                        <>
                                            <span className={classes.countBadge} aria-hidden="true">{openTenders}</span>
                                            <span className={classes.srOnly}>{`, ${tenderCountLabel}`}</span>
                                        </>
                                    )}
                                </Button>
                            ))}

                            <span className={classes.navSpacer} />

                            {openConsultations > 0 && (
                                <Button
                                    className={classes.consultPill}
                                    href={getMainPortalUrl('/consultations')}
                                    title={`${t('Public consultations')}: ${consultationCountLabel}`}
                                >
                                    <span className={classes.liveDot} aria-hidden="true" />
                                    {t('Have your say')}
                                    <span className={classes.countBadge} aria-hidden="true">{openConsultations}</span>
                                    <span className={classes.srOnly}>{`, ${t('Public consultations')}: ${consultationCountLabel}`}</span>
                                </Button>
                            )}

                            {subdomain && (
                                <Button
                                    className={classes.navPortalLink}
                                    href={getMainPortalUrl('/')}
                                >
                                    Back to main portal
                                </Button>
                            )}
                        </nav>
                    </Container>
                </div>

                <Popper
                    open={Boolean(activeItem && activeItem.columns)}
                    anchorEl={anchorNode}
                    placement="bottom-start"
                    className={classes.popper}
                    modifiers={{ flip: { enabled: false }, preventOverflow: { enabled: false } }}
                >
                    <ClickAwayListener onClickAway={closeMenu}>
                        <Paper
                            className={classes.megaPanel}
                            onMouseEnter={cancelClose}
                            onMouseLeave={scheduleClose}
                        >
                            <Container maxWidth="lg">
                                {activeItem && (
                                    <Grid container spacing={4}>
                                        <Grid item md={3}>
                                            <div className={classes.megaIntro}>
                                                <Typography
                                                    variant="h6"
                                                    component="p"
                                                    className={classes.megaIntroTitle}
                                                >
                                                    {t(activeItem.label)}
                                                </Typography>
                                                {activeItem.description && (
                                                    <p className={classes.megaIntroText}>
                                                        {t(activeItem.description)}
                                                    </p>
                                                )}
                                                {isProcurementItem(activeItem) && openTenders > 0 && (
                                                    <a
                                                        className={classes.megaHighlight}
                                                        href={getMainPortalUrl('/tenders')}
                                                        onClick={closeMenu}
                                                        aria-label={`${t('Open tenders')}: ${tenderCountLabel}`}
                                                    >
                                                        {t('Open tenders')}
                                                        <span className={classes.countBadge} aria-hidden="true">{openTenders}</span>
                                                    </a>
                                                )}
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    href={menuLinkUrl(activeItem.to)}
                                                    onClick={closeMenu}
                                                >
                                                    Go to section
                                                </Button>
                                            </div>
                                        </Grid>

                                        {activeItem.columns.map((column) => (
                                            <Grid
                                                item
                                                md={Math.max(2, Math.floor(9 / activeItem.columns.length))}
                                                key={column.title}
                                            >
                                                <p className={classes.megaColumnTitle}>{t(column.title)}</p>
                                                <ul className={classes.megaList}>
                                                    {column.links.map((link) => (
                                                        <li key={link.label} className={classes.megaItem}>
                                                            <NavLink
                                                                link={link}
                                                                className={classes.megaLink}
                                                                iconClassName={classes.externalIcon}
                                                                onClick={closeMenu}
                                                            />
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Container>
                        </Paper>
                    </ClickAwayListener>
                </Popper>
            </AppBar>

            {/* Mobile navigation */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                classes={{ paper: classes.drawerPaper }}
            >
                <div className={classes.drawerHeader}>
                    <span className={classes.drawerTitle}>Ministry of Finance</span>
                    <IconButton
                        aria-label="Close menu"
                        onClick={() => setDrawerOpen(false)}
                        style={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>

                <NavbarSearch id="drawer-search" mobile onNavigate={navigateSearch} />

                <List component="nav" aria-label="Main navigation">
                    {openConsultations > 0 && (
                        <>
                            <ListItem
                                button
                                component="a"
                                href={getMainPortalUrl('/consultations')}
                                onClick={() => setDrawerOpen(false)}
                            >
                                <span className={classes.liveDot} aria-hidden="true" />
                                <ListItemText
                                    primary={t('Have your say')}
                                    secondary={`${t('Public consultations')}: ${consultationCountLabel}`}
                                    primaryTypographyProps={{ style: { fontWeight: 700 } }}
                                />
                                <span className={classes.countBadge} aria-hidden="true">{openConsultations}</span>
                            </ListItem>
                            <Divider />
                        </>
                    )}
                    {dynamicNavigation.map((item) => {
                        const hasChildren = Boolean(item.columns);
                        const isOpen = expandedSection === item.id;

                        return (
                            <React.Fragment key={item.id}>
                                <ListItem
                                    button
                                    onClick={() => {
                                        if (hasChildren) {
                                            setExpandedSection(isOpen ? null : item.id);
                                        } else {
                                            setDrawerOpen(false);
                                            history.push(item.to);
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={t(item.label)}
                                        primaryTypographyProps={{ style: { fontWeight: 600 } }}
                                    />
                                    {isProcurementItem(item) && openTenders > 0 && (
                                        <span className={classes.countBadge} aria-label={tenderCountLabel} style={{ marginRight: 8 }}>
                                            {openTenders}
                                        </span>
                                    )}
                                    {hasChildren && (isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                                </ListItem>

                                {hasChildren && (
                                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                        <ListItem
                                            button
                                            className={classes.drawerSubLink}
                                            onClick={() => {
                                                setDrawerOpen(false);
                                                history.push(item.to);
                                            }}
                                        >
                                            <ListItemText
                                                    primary={`${t(item.label)} overview`}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                        </ListItem>
                                        {isProcurementItem(item) && openTenders > 0 && (
                                            <ListItem
                                                button
                                                className={classes.drawerSubLink}
                                                component="a"
                                                href={getMainPortalUrl('/tenders')}
                                                onClick={() => setDrawerOpen(false)}
                                            >
                                                <ListItemText
                                                    primary={t('Open tenders')}
                                                    primaryTypographyProps={{ variant: 'body2', style: { fontWeight: 700 } }}
                                                />
                                                <span className={classes.countBadge} aria-label={tenderCountLabel}>{openTenders}</span>
                                            </ListItem>
                                        )}
                                        {item.columns.map((column) =>
                                            column.links.map((link) => (
                                                <ListItem
                                                    key={`${column.title}-${link.label}`}
                                                    button
                                                    className={classes.drawerSubLink}
                                                    component={link.href ? 'a' : RouterLink}
                                                    href={link.href}
                                                    to={link.to}
                                                    target={link.href ? '_blank' : undefined}
                                                    rel={link.href ? 'noopener noreferrer' : undefined}
                                                    onClick={() => setDrawerOpen(false)}
                                                >
                                                    <ListItemText
                                                        primary={t(link.label)}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))
                                        )}
                                    </Collapse>
                                )}
                                <Divider />
                            </React.Fragment>
                        );
                    })}
                </List>

                <p className={classes.drawerSectionLabel}>Related government sites</p>
                <List>
                    {relatedSites.map((site) => (
                        <ListItem
                            key={site.label}
                            button
                            component="a"
                            href={site.href}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <ListItemText
                                primary={t(site.label)}
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                            <OpenInNewIcon style={{ fontSize: 14, color: tokens.muted }} />
                        </ListItem>
                    ))}
                    {subdomain && (
                        <ListItem button component="a" href={getMainPortalUrl('/')}>
                            <ListItemText primary="Back to main portal" />
                        </ListItem>
                    )}
                </List>
            </Drawer>
        </div>
    );
}
